import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/Input/index";
import { Button } from "@/components/ui/Button/index";
import { Field, Form, Formik } from "formik";
import { FormContainer, FormItem } from "@/components/ui/Form/index";
import * as Yup from "yup";
import { CommonProps } from "@/@types/common";
import AIButton from "@/components/ui/AIButton";
import { apiGenerateShred, apiGetShredProgress, apiRegeneratePlan, apiUpdatePlan } from "@/services/ShredService";
import { ShredResponse, UpdatePlanRequest } from "@/@types/shred";
import CKEditorReadonly from "@/components/CKEditorReadonly.tsx";
import { AlertCircle, Sparkles, X, Bell } from "lucide-react";
import { apiGetUserHasToken, apiSaveUserToken } from "@/services/UserService";
import { apiValidateShredForGeneration } from "@/services/ShredService";

interface StepFourProps extends CommonProps {
  onStepValidationResponse: (response: ShredResponse) => void;
  onStepError: (error: string) => void;
  shredId: string;
  shredName: string;
  shredDescription: string;
  shredPlan: string;
}

// --- GitHub Token Dialog Component ---
interface GitHubTokenDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onTokenSaved: (values: GitHubTokenFormSchema) => void;
}

type GitHubTokenFormSchema = {
  token: string;
  endPoint: string;
}

const GitHubTokenDialog: FC<GitHubTokenDialogProps> = ({ isOpen, onClose, onContinue, onTokenSaved }) => {
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); // Add local error state
  
  const validationSchema = Yup.object().shape({
    token: Yup.string().required("GitHub token is required"),
    endPoint: Yup.string().when([], {
      is: () => showEnterprise,
      then: (schema) => schema.url("Please enter a valid URL"),
      otherwise: (schema) => schema.notRequired()
    })
  });
  
  const handleGithubTokenCreation = () => {
    const githubTokenUrl = "https://github.com/settings/tokens/new?scopes=repo,delete_repo&description=API%20Access%20Token";
    window.open(githubTokenUrl, "_blank");
  };
  
  const handleSubmit = async (values: GitHubTokenFormSchema) => {
    setIsSubmitting(true);
    setErrorMessage(""); // Clear any previous errors
    
    try {
      await onTokenSaved(values);
    } catch (error) {
      console.log("Error saving token:", error);
      setErrorMessage("Failed to save GitHub token. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-4 md:p-5 max-h-[140vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-100 rounded-full">
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
            </div>
            <h3 className="text-sm md:text-base font-bold text-gray-900">GitHub Token Missing</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="mb-4">
          <div className="space-y-3">
            {/* Add error display within the dialog */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
            
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
              You must either set a GitHub token so that you can securely push the code to your repository
            </p>
            
            <Formik
              initialValues={{ token: "", endPoint: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched }) => (
                <Form className="space-y-3">
                  {/* Rest of the form remains the same */}
                  <FormItem
                    label="GitHub Token"
                    labelClass="text-sm text-[#6F6AF8] font-medium mb-1"
                    htmlFor="token"
                    invalid={!!(errors.token && touched.token)}
                    errorMessage={errors.token}
                  >
                    <Field
                      autoComplete="off"
                      name="token"
                      placeholder="Enter your token"
                      size="sm"
                      component={Input}
                      className="text-sm py-2 px-3 border-gray-300 rounded-md"
                    />
                    
                    <div className="flex items-center justify-end mt-1">
                      <Button
                        type="button"
                        variant="plain"
                        size="xs"
                        onClick={handleGithubTokenCreation}
                        className="text-indigo-600 hover:text-indigo-700 text-xs flex items-center py-1"
                      >
                        Create GitHub Token
                      </Button>
                    </div>
                  </FormItem>
                  
                  {/* Conditional Enterprise URL Input */}
                  {showEnterprise && (
                    <FormItem
                      label="Enterprise Endpoint"
                      labelClass="text-sm text-[#6F6AF8] font-medium mb-1"
                      htmlFor="endPoint"
                      invalid={!!(errors.endPoint && touched.endPoint)}
                      errorMessage={errors.endPoint}
                    >
                      <Field
                        type="url"
                        autoComplete="off"
                        name="endPoint"
                        size="sm"
                        placeholder="e.g. 'https://api.github.example.com'"
                        component={Input}
                        className="text-sm py-2 px-3 border-gray-300 rounded-md"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Enter the URL for your GitHub Enterprise API endpoint
                      </div>
                    </FormItem>
                  )}
                  
                  {/* Enterprise Toggle Button */}
                  <div className="flex justify-center mb-3">
                    <Button
                      type="button"
                      variant="plain"
                      size="xs"
                      onClick={() => setShowEnterprise(!showEnterprise)}
                      className="text-blue-600 hover:text-blue-700 text-xs flex items-center py-1"
                    >
                      {showEnterprise ? "I don't have GitHub Enterprise" : "I have GitHub Enterprise"}
                    </Button>
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-2">
                    <Button
                      loading={isSubmitting}
                      variant="solid"
                      color="green-600"
                      className="px-3 py-2 text-white bg-green-600 hover:bg-green-700 border border-green-600 rounded-md font-medium transition-colors text-xs"
                      type="submit"
                    >
                      {isSubmitting ? "Saving..." : "Save Token & Generate"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
            
            {/* Rest of the dialog content remains the same */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-gray-500 font-medium text-xs px-2">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Accept that the code will be pushed to a public repository (which is fine for open-source projects or
                for testing ShedsAI)
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                <p className="text-blue-700 text-xs font-medium">
                  Notice: For public repositories, you can't benefit from ShedsAI's Pull Request processing feature
                  since you don't own the repository
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                onClick={onContinue}
                variant="solid"
                color="gray-700"
                disabled={isSubmitting}
                className="px-3 py-2 border border-gray-300 rounded-md font-medium transition-colors text-xs"
              >
                Continue Anyway
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Step Four Component ---

type UpdatePlanFormSchema = {
  modifications: string
}

const StepFour: FC<StepFourProps> = (props) => {
  const {
    shredId,
    shredName,
    shredDescription,
    shredPlan,
    onStepValidationResponse,
    onStepError
  } = props;
  
  const [validateShred, setValidateShred] = useState(false);
  const [plan, setPlan] = useState(shredPlan);
  
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true); // New state for initial loading
  
  const [regeneratePlanIsLoading, setRegeneratePlanIsLoading] = useState(false);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  
  // Add subscription check hook
  
  const validationSchema = Yup.object().shape({
    modifications: Yup.string().required("Please enter the modifications you want to apply to the plan.")
  });
  
  // Replace the triggerTour function with this enhanced version that includes more debugging:
  const triggerTour = useCallback(() => {
    console.log("🎯 Triggering tour refresh...");
    
    // First, clear any dismissed state for this page since we're showing new content
    if (typeof window !== 'undefined' && (window as any).clearTourDismissed) {
      console.log("🎯 Clearing dismissed tour state");
      (window as any).clearTourDismissed();
    }
    
    // Check if the element exists before triggering refresh
    const tourElement = document.querySelector('[data-tour="generate-shred"]');
    console.log("🎯 Tour element exists before refresh:", !!tourElement, tourElement);
    
    // Then call the SimpleTour refresh method
    if (typeof window !== 'undefined' && (window as any).refreshTour) {
      console.log("🎯 Calling SimpleTour refresh method");
      (window as any).refreshTour();
      
      // Check again after refresh
      setTimeout(() => {
        const tourElementAfter = document.querySelector('[data-tour="generate-shred"]');
        console.log("🎯 Tour element exists after refresh:", !!tourElementAfter, tourElementAfter);
        
        // Force a manual scan to see what's happening
        if (typeof window !== 'undefined' && (window as any).debugTourScan) {
          console.log("🎯 Running debug tour scan");
          (window as any).debugTourScan();
        }
      }, 100);
    } else {
      console.log("🎯 SimpleTour refresh method not available");
    }
  }, []);
  
  const getStatus = () => {
    onStepError("");
    
    apiGetShredProgress(shredId)
      .then(value => {
        if (value.data.body.status === "PLAN_GENERATED") {
          setHasError(false);
          setInProgress(false);
          setPlan(value.data.body.htmlPlan);
          
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          
          console.log("🎯 Plan generated - triggering tour refresh...");
          
          // Wait for React to complete the re-render, then refresh the tour
          setTimeout(() => {
            console.log("🎯 Tour refresh attempt 1 (100ms)");
            triggerTour();
          }, 500);
          
        } else if (value.data.body.status === "PLAN_FAILED") {
          setHasError(true);
          setInProgress(false);
          setPlan(value.data.body.htmlPlan);
          
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        } else {
          setHasError(false);
          setInProgress(true);
        }
      })
      .catch(() => {
        setHasError(true);
        setInProgress(false);
        
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        onStepError("An error occurred while fetching the shred progress");
      })
      .finally(() => {
        setIsInitialLoading(false); // Stop initial loading after first API response
      });
  };
  
  const onUpdatePlan = (
    values: UpdatePlanFormSchema,
    setSubmitting: (isSubmitting: boolean) => void,
    setFieldValue: (field: string, value: string | undefined, shouldValidate?: boolean) => void
  ) => {
    const { modifications } = values;
    setSubmitting(true);
    setFieldValue("modifications", "", false);
    
    apiUpdatePlan<ShredResponse, UpdatePlanRequest>(shredId, { modifications })
      .then(() => {
        setInProgress(true);
        createInterval();
      })
      .catch(() => {
        setHasError(true);
        onStepError("An error occurred while updating the plan");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  
  const onRegeneratePlan = () => {
    setRegeneratePlanIsLoading(true);
    
    apiRegeneratePlan(shredId)
      .then(() => {
        setInProgress(true);
        createInterval();
      })
      .catch(() => {
        setHasError(true);
        onStepError("An error occurred while regenerating the plan");
      })
      .finally(() => {
        setRegeneratePlanIsLoading(false);
      });
  };
  
  const onGenerateShred = () => {
    setValidateShred(true);
    
    // Validate the shred before proceeding
    apiValidateShredForGeneration(shredId)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(`Validation failed with status code ${response.status}`);
        }
        
        // Check if user has GitHub token
        apiGetUserHasToken()
          .then((response) => {
            if (!response.data) {
              setValidateShred(false);
              setShowTokenDialog(true);
              return; // Stop the form submission
            } else {
              generateShred();
            }
          })
          .catch((err) => {
            if ("response" in err && "data" in err.response) {
              if ("body" in err.response.data) {
                onStepError(err.response.data.body.message);
              } else {
                onStepError(err.response.data.message);
              }
            } else {
              onStepError("An error occurred while starting the shred generation");
            }
          })
      })
      .catch((err) => {
        if ("response" in err && "data" in err.response) {
          if ("body" in err.response.data) {
            onStepError(err.response.data.body.message);
          } else {
            onStepError(err.response.data.message);
          }
        } else {
          onStepError("An error occurred while validating the shred");
        }
      })
      .finally(() => {
        setValidateShred(false);
      });
  };
  
  const generateShred = () => {
    setValidateShred(false);
    
    apiGenerateShred(shredId)
      .then((result) => {
        onStepValidationResponse(result.data);
      })
      .catch((err) => {
        if ("response" in err && "data" in err.response) {
          if ("body" in err.response.data) {
            onStepError(err.response.data.body.message);
          } else {
            onStepError(err.response.data.message);
          }
        } else {
          onStepError("An error occurred while generating the shred");
        }
      })
      .finally(() => {
        setValidateShred(false);
      });
  };
  
  const handleContinueAnyway = () => {
    setShowTokenDialog(false);
    generateShred();
  };
  
  const handleTokenSaved = async (values: GitHubTokenFormSchema) => {
    try {
      await apiSaveUserToken({ token: values.token, endPoint: values.endPoint });
      setShowTokenDialog(false);
      generateShred();
    } catch (error) {
      console.error("Error saving token:", error);
      throw new Error("Failed to save GitHub token");
    }
  };
  
  const formatPreTags = (htmlString: string) => {
    return htmlString.replace(
      /<pre class="(.*?)">(.*?)<\/pre>/gs,
      (_match, attributes, content) => {
        let formattedContent = content;
        
        try {
          const jsonContent = JSON.parse(content);
          formattedContent = JSON.stringify(jsonContent, null, 2);
        } catch (e) {
          // If it's not valid JSON, leave it as is
        }
        
        return `<pre class="whitespace-pre p-4 bg-gray-100 rounded-md overflow-x-auto text-sm font-mono ${attributes}">${formattedContent}</pre>`;
      }
    ).replace(
      /<pre>(.*?)<\/pre>/gs,
      (_match, content) => {
        let formattedContent = content;
        
        try {
          const jsonContent = JSON.parse(content);
          formattedContent = JSON.stringify(jsonContent, null, 2);
        } catch (e) {
          // If it's not valid JSON, leave it as is
        }
        
        return `<pre class="whitespace-pre p-4 bg-gray-100 rounded-md overflow-x-auto text-sm font-mono">${formattedContent}</pre>`;
      }
    );
  };
  
  const createInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => getStatus(), 2000);
  };
  
  const intervalRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    createInterval();
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
  
  // Loading Animation Component
  const AILoadingAnimation = () => (
    <div className="flex flex-col items-center justify-center py-12 md:py-16 px-4 md:px-8">
      {/* Neural Network Loader */}
      <div className="relative mb-6 md:mb-8">
        <div className="w-16 h-16 md:w-24 md:h-24 relative">
          {/* Outer rotating circle */}
          <div
            className="absolute inset-0 border-4 border-gradient-to-r from-purple-400 via-blue-500 to-cyan-400 rounded-full animate-spin opacity-75"></div>
          {/* Inner pulsing circle */}
          <div
            className="absolute inset-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full animate-pulse opacity-80"></div>
          {/* Center dot */}
          <div className="absolute inset-4 md:inset-8 bg-white rounded-full shadow-lg"></div>
          {/* Neural connections */}
          <div className="absolute -inset-4">
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-400 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2 animate-ping"></div>
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-400 rounded-full absolute bottom-0 left-1/2 transform -translate-x-1/2 animate-ping delay-200"></div>
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 bg-cyan-400 rounded-full absolute left-0 top-1/2 transform -translate-y-1/2 animate-ping delay-400"></div>
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 bg-pink-400 rounded-full absolute right-0 top-1/2 transform -translate-y-1/2 animate-ping delay-600"></div>
          </div>
        </div>
      </div>
      
      {/* AI Brain Animation */}
      <div className="relative mb-4 md:mb-6">
        <div className="flex space-x-2">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-bounce"></div>
          <div
            className="w-2.5 h-2.5 md:w-3 md:h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-bounce delay-100"></div>
          <div
            className="w-2.5 h-2.5 md:w-3 md:h-3 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full animate-bounce delay-200"></div>
        </div>
      </div>
      
      {/* Gradient Text */}
      <h3
        className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3 text-center">
        {isInitialLoading ? "Loading Plan Status..." : "AI is Crafting Your Plan"}
      </h3>
      
      {/* Animated description */}
      <p className="text-gray-600 text-center max-w-sm md:max-w-md leading-relaxed mb-4 md:mb-6 text-sm md:text-base px-2">
        {isInitialLoading
          ? "Checking the current status of your plan generation..."
          : "Our advanced AI is currently analyzing your inputs and formulating a detailed, customized blueprint that outlines the full process it will follow to generate this Shred."
        }
      </p>
      
      {/* Progress steps */}
      <div className="flex items-center justify-center space-x-2 md:space-x-4 mb-6 md:mb-8 overflow-x-auto px-2">
        <div className="flex items-center space-x-1 md:space-x-2 min-w-0">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-green-500 rounded-full flex-shrink-0"></div>
          <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Processing</span>
        </div>
        <div className="w-4 md:w-8 h-0.5 bg-gradient-to-r from-green-500 to-blue-500 animate-pulse flex-shrink-0"></div>
        <div className="flex items-center space-x-1 md:space-x-2 min-w-0">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-blue-500 rounded-full animate-pulse flex-shrink-0"></div>
          <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">Generating</span>
        </div>
        <div className="w-4 md:w-8 h-0.5 bg-gray-300 flex-shrink-0"></div>
        <div className="flex items-center space-x-1 md:space-x-2 min-w-0">
          <div className="w-2.5 h-2.5 md:w-3 md:h-3 bg-gray-300 rounded-full flex-shrink-0"></div>
          <span className="text-xs md:text-sm text-gray-400 whitespace-nowrap">Complete</span>
        </div>
      </div>
      
      {/* Time estimate */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-4 md:px-6 py-2 md:py-3 rounded-full border border-purple-100">
        <p className="text-xs md:text-sm text-purple-700 font-medium">
          ⏱️ This usually takes 2-5 minutes
        </p>
      </div>
    </div>
  );
  
  return (
    <div className="space-y-6">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {(isInitialLoading || inProgress) ? (
          // AI Loading State (including initial loading)
          <div className="container mx-auto px-4 py-4 md:py-8">
            {/* Notification Box */}
            <div className="max-w-2xl mx-auto mb-6 mt-6 md:mt-8">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-200 shadow-lg">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-xl animate-pulse"></div>
                  <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-tr from-indigo-200/30 to-blue-200/30 rounded-full blur-xl animate-pulse delay-700"></div>
                </div>
                
                <div className="relative p-4 md:p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg animate-bounce">
                        <Bell className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-base md:text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-2">
                        We'll Notify You When It's Ready!
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">
                        Your plan is being generated by our AI. You'll receive a notification as soon as it's complete.
                      </p>
                      
                      <div className="flex flex-wrap gap-2 md:gap-3">
                        <div className="inline-flex items-center px-2.5 md:px-3 py-1 md:py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium">
                          <div className="w-1.5 md:w-2 h-1.5 md:h-2 bg-blue-500 rounded-full mr-1.5 md:mr-2 animate-pulse"></div>
                          Email Notification
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-6 md:mb-8">
                <h1
                  className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  Plan Generation
                </h1>
                <p className="text-sm md:text-base text-gray-600 px-2">
                  Creating your personalized shred plan: <span
                  className="font-semibold text-purple-600">{shredName}</span>
                </p>
              </div>
              
              {/* AI Loading Card */}
              <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 h-2"></div>
                <AILoadingAnimation />
              </div>
              
              {/* Quick Info Cards - Only show when not initial loading */}
              {!isInitialLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6 md:mt-8">
                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-gray-100">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                      Shred Overview
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Name</p>
                        <p className="font-medium text-gray-800 text-sm md:text-base break-words">{shredName}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-gray-100">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                      What's Happening?
                    </h3>
                    <div className="space-y-2 text-xs md:text-sm text-gray-600">
                      <p>✨ Analyzing your requirements</p>
                      <p>🧠 Summarizing the Shred specs</p>
                      <p>📋 Selecting the best patterns</p>
                      <p>🎯 Organizing the workflows</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Normal Form State - Full Width
          <div className="px-2 md:px-0">
            <div className="mx-auto w-full">
              <Formik
                initialValues={{ modifications: "" }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, setFieldValue }) => {
                  onUpdatePlan(values, setSubmitting, setFieldValue);
                }}
              >
                {({ touched, errors, isSubmitting }) => (
                  <Form>
                    <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 h-2"></div>
                      
                      <div className="p-4 md:p-8">
                        <FormContainer>
                          {/* Header */}
                          <div className="text-center mb-6 md:mb-8">
                            <h1
                              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
                              Review Your Plan
                            </h1>
                            <p className="text-sm md:text-base text-gray-600">Review and customize your AI-generated plan</p>
                          </div>
                          
                          {/* Shred Info Cards */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                            <div
                              className="bg-gradient-to-br from-purple-50 to-blue-50 p-4 md:p-6 rounded-xl border border-purple-100">
                              <FormItem
                                label="Shred Name"
                                labelClass="text-base md:text-lg text-purple-700 font-semibold mb-3"
                                htmlFor="shredName"
                              >
                                <div className="bg-white border border-purple-200 p-3 md:p-4 rounded-lg shadow-sm">
                                  <p className="text-gray-800 font-medium text-sm md:text-base break-words">{shredName}</p>
                                </div>
                              </FormItem>
                            </div>
                            
                            <div
                              className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 md:p-6 rounded-xl border border-blue-100">
                              <FormItem
                                label="Description"
                                labelClass="text-base md:text-lg text-blue-700 font-semibold mb-3"
                                htmlFor="shredDescription"
                              >
                                <div className="bg-white rounded-lg border border-blue-200 overflow-hidden shadow-sm">
                                  <CKEditorReadonly content={shredDescription} />
                                </div>
                              </FormItem>
                            </div>
                          </div>
                          
                          {/* Error Alert */}
                          {hasError && (
                            <div className="mb-4 md:mb-6">
                              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <div className="flex items-center">
                                  <div
                                    className="w-6 h-6 md:w-8 md:h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                    <span className="text-red-600 text-xs md:text-sm">⚠️</span>
                                  </div>
                                  <div>
                                    <h3 className="text-red-800 font-semibold text-sm md:text-base">Generation Failed</h3>
                                    <p className="text-red-600 text-xs md:text-sm">An error occurred while generating the plan.
                                      Please try again.</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Generated Plan */}
                          {plan && !hasError && (
                            <div className="mb-6 md:mb-8">
                              <div
                                className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 md:p-6 rounded-xl border border-green-200">
                                <h2 className="text-lg md:text-xl font-bold text-green-800 mb-4 flex items-center">
                                  <span className="w-5 h-5 md:w-6 md:h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                      <span className="text-green-600 text-xs md:text-sm">✓</span>
                                  </span>
                                  Generated Plan
                                </h2>
                                <div className="bg-white rounded-lg border border-green-200 shadow-sm overflow-x-auto">
                                  <div
                                    className="p-4 md:p-6 text-gray-800 text-sm md:text-base"
                                    dangerouslySetInnerHTML={{ __html: formatPreTags(plan) }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Regenerate Plan Button */}
                          {hasError && (
                            <div className="flex justify-center mb-4 md:mb-6">
                              <Button
                                loading={regeneratePlanIsLoading}
                                size="lg"
                                variant="solid"
                                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl font-semibold shadow-lg transition-all duration-200 text-sm md:text-base"
                                type="button"
                                onClick={() => onRegeneratePlan()}
                              >
                                {regeneratePlanIsLoading ? "Regenerating..." : "🔄 Regenerate Plan"}
                              </Button>
                            </div>
                          )}
                          
                          {/* Plan Modifications */}
                          <div className="mb-6 md:mb-8">
                            <div
                              className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 md:p-6 rounded-xl border border-yellow-200">
                              <FormItem
                                label="Plan Modifications (Optional)"
                                labelClass="text-base md:text-lg text-orange-700 font-semibold mb-3"
                                invalid={(errors.modifications && touched.modifications) as boolean}
                                htmlFor="modifications"
                                errorMessage={errors.modifications}
                              >
                                <Field
                                  type="text"
                                  autoComplete="off"
                                  name="modifications"
                                  textArea={true}
                                  placeholder="Describe any changes you'd like to make to the generated plan..."
                                  component={Input}
                                  className="border-orange-200 focus:border-orange-400 focus:ring-orange-200 text-sm md:text-base"
                                />
                              </FormItem>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-6">
                            {/* Clear Action Header */}
                            <div className="text-center">
                              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">Choose Your Next Step</h3>
                              <p className="text-sm md:text-base text-gray-600">You can modify the plan or proceed to code generation</p>
                            </div>

                            {/* Desktop Action Cards */}
                            <div className="hidden lg:grid lg:grid-cols-2 gap-6">
                              {/* Update Plan Card */}
                              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 relative group hover:border-amber-300 transition-all duration-200">
                                <div className="flex items-start gap-4 mb-4">
                                  <div className="p-3 bg-amber-100 rounded-full group-hover:bg-amber-200 transition-colors">
                                    <span className="text-2xl">✏️</span>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-lg font-semibold text-amber-800 mb-2">Option 1: Modify Plan</h4>
                                    <p className="text-sm text-amber-700 leading-relaxed">
                                      Make changes to the generated plan by describing your modifications in the text area above, then click the button below to update.
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  loading={isSubmitting}
                                  disabled={validateShred || hasError}
                                  variant="solid"
                                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-3 rounded-xl font-semibold shadow-lg transition-all duration-200 text-sm md:text-base whitespace-nowrap"
                                  type="submit"
                                >
                                  {isSubmitting ? "⚡ Updating Plan..." : "✏️ Update Plan with Modifications"}
                                </Button>
                              </div>

                              {/* Generate Code Card */}
                              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 relative group hover:border-purple-300 transition-all duration-200">
                                <div className="flex items-start gap-4 mb-4">
                                  <div className="p-3 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                                    <Sparkles className="w-6 h-6 text-purple-600" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-lg font-semibold text-purple-800 mb-2">Option 2: Generate Code</h4>
                                    <p className="text-sm text-purple-700 leading-relaxed">
                                      Proceed with the current plan to start the AI code generation process. This will consume credits and create your shred.
                                    </p>
                                  </div>
                                </div>
                                <AIButton
                                  data-tour="generate-shred"
                                  onClick={() => {
                                    setShowCreditWarning(true);
                                  }}
                                  loading={validateShred}
                                  disabled={isSubmitting || hasError}
                                  variant="primary-cosmic"
                                  size="lg"
                                  showSparkles={true}
                                  showArrow={true}
                                  glowIntensity="high"
                                  icon={<Sparkles />}
                                  className="w-full"
                                >
                                  {validateShred ? "Generating The Code..." : "🚀 Generate The Code"}
                                </AIButton>
                              </div>
                            </div>

                            {/* Mobile Action Layout */}
                            <div className="lg:hidden bg-white p-3 rounded-lg border border-gray-200">
                              {/* Update Plan Mobile */}
                              <div className="mb-4">
                                <h4 className="text-base font-semibold text-amber-800 mb-1">✏️ Option 1: Modify Plan</h4>
                                <p className="text-sm text-amber-700 mb-3">Make changes using the modifications field above</p>
                                <Button
                                  loading={isSubmitting}
                                  disabled={validateShred || hasError}
                                  variant="solid"
                                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-4 rounded-lg font-semibold shadow-lg transition-all duration-200 text-base"
                                  type="submit"
                                >
                                  {isSubmitting ? "⚡ Updating Plan..." : "✏️ Update Plan"}
                                </Button>
                              </div>

                              {/* Divider */}
                              <div className="flex items-center gap-2 py-3">
                                <div className="flex-1 border-t border-gray-300"></div>
                                <span className="text-gray-500 font-medium text-sm">OR</span>
                                <div className="flex-1 border-t border-gray-300"></div>
                              </div>

                              {/* Generate Code Mobile */}
                              <div>
                                <h4 className="text-base font-semibold text-purple-800 mb-1">🚀 Option 2: Generate Code</h4>
                                <p className="text-sm text-purple-700 mb-3">Proceed with current plan to generate code</p>
                                <AIButton
                                  data-tour="generate-shred"
                                  onClick={() => {
                                    setShowCreditWarning(true);
                                  }}
                                  loading={validateShred}
                                  disabled={isSubmitting || hasError}
                                  variant="primary-cosmic"
                                  size="lg"
                                  showSparkles={true}
                                  showArrow={true}
                                  glowIntensity="high"
                                  icon={<Sparkles />}
                                  className="w-full"
                                >
                                  {validateShred ? "Generating..." : "🚀 Generate Code"}
                                </AIButton>
                              </div>
                            </div>

                            {/* Help Text */}
                            <div className="text-center">
                              <p className="text-xs md:text-sm text-gray-500 max-w-2xl mx-auto">
                                💡 <strong>Tip:</strong> If you're satisfied with the generated plan, click "Generate The Code" to proceed.
                                If you want to make changes, describe them in the modifications field above and click "Update Plan".
                              </p>
                            </div>
                          </div>
                        </FormContainer>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
        
        {/* Credit Warning Modal */}
        {showCreditWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 h-2"></div>
              
              <div className="p-4 md:p-6">
                <div className="flex items-center mb-4">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-full flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                    <span className="text-orange-600 text-xl md:text-2xl">⚠️</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-800">Confirm Start Code Generation</h3>
                  </div>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                  <p className="text-gray-700 mb-3 text-sm md:text-base">
                    Starting the code generation cycle will:
                  </p>
                  <ul className="space-y-2 text-xs md:text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <div>
                        <span>Consume credits from your account (starting from 350 credits per shred)</span>
                        <br />
                        <span>You can cancel the generation at any time during the process.</span>
                      </div>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-3 flex-shrink-0"></span>
                      Begin the full shred generation process
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-3 flex-shrink-0"></span>
                      You cannot modify this plan once the generation starts
                    </li>
                  </ul>
                </div>
                
                <p className="text-gray-600 text-xs md:text-sm mb-4 md:mb-6">
                  Are you sure you want to proceed with generating the shred for <span
                  className="font-semibold text-purple-600 break-words">"{shredName}"</span>?
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowCreditWarning(false)}
                    variant="solid"
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition-colors duration-200 text-sm md:text-base"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCreditWarning(false);
                      onGenerateShred();
                    }}
                    variant="solid"
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium shadow-lg transition-all duration-200 text-sm md:text-base"
                    type="button"
                  >
                    <span className="hidden sm:inline">Yes, Generate Shred</span>
                    <span className="sm:hidden">Generate</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* GitHub Token Dialog */}
        <GitHubTokenDialog
          isOpen={showTokenDialog}
          onClose={() => {
            setShowTokenDialog(false);
          }}
          onContinue={handleContinueAnyway}
          onTokenSaved={handleTokenSaved}
        />
      </div>
    </div>
  );
};

export default StepFour;